import type { VFC } from 'react'
import type { StoreState } from '@sparky/framework'
import {
  ConnectedTable,
  ConnectedTableLayout,
  HeaderTitle,
  TableProvider
} from '@panwds/react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import LicenseRenderer from './LicenseRenderer'
import StatusRenderer from './StatusRenderer'
import styled from 'styled-components'
import ProductNameRenderer from './ProductNameRenderer'
import { Body } from '@panwds/react-ui'
import { transferLicenseToPA } from '../common/utils'
import type { ComponentDef } from '../../renderer'
import { format, parseISO } from 'date-fns'
import { upperFirst } from 'lodash'
import { BUNDLED_PRODUCTS } from '../../../common/constants/LabelConstants'
import { isExpiredDate } from '../../../common/utils/Utils'

const TableWrapper = styled.div`
  div[data-testid="connected-table-layout"] {
    height: 100% !important;
    max-height: 250px !important;
  }
   div[role="row"] {
    height: auto !important;
    padding-bottom:4px;
  }
  div[role="rowgroup"] {
    border-bottom: none;
    z-index: 1
  }
  div {
    min-height:0px !important;
  }
   svg {
      max-width: 5rem !important
   }
  padding-top:10px;
  `

type InstanceTableProps = {
  children: ComponentDef | ComponentDef[] | undefined
  state: StoreState
  title: string,
  primaryButton: {
    text: string
  }
}
const InstanceTable: VFC<InstanceTableProps> = ({ state, ...props }) => {
  const { instances, apps, tsg, platformRegions, loadingTsg } = state
  const { title } = props
  const [tableData, setTableData] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(false)
  useEffect(() => {
    setTableData(loadingTsg || !tsg ? [] : [...instances.filter((instance: any) => !Boolean(instance?.runtime_attributes?.hidden))])
    setLoading(loadingTsg && tsg)
  }, [instances, loadingTsg, tsg])

  const getRegionDisplayName = useCallback((row: any) => {
    const region = row?.original?.platform_region ?? row?.original?.region
    const regionName = platformRegions.find((each: any) => each.name === region)
    return upperFirst(regionName?.display ?? region)
  }, [platformRegions])

  const getExpirationDates = useCallback(({ row, value }) => {
    let className = value && isExpiredDate(value) ? 'tw-text-error' : ''
    const { app_id, entitlements } = row?.original
    const bundledProduct = BUNDLED_PRODUCTS.has(app_id) && entitlements?.length > 1
    if (bundledProduct) {
      return (<div className='tw-text-sm tw-block'>
        {entitlements?.map((each: any, index: any) => {
          className = isExpiredDate(each.license_expiration) ? 'tw-text-error' : ''
          return (<div key={index}><Body addClassName={className} size='xs'>{each.license_expiration ? format(parseISO(each.license_expiration), 'MM/dd/yyyy') : 'NA'}</Body></div>)
        })}
      </div>)
    }
    return <Body addClassName={className} size='xs'>{value ? format(parseISO(value), 'MM/dd/yyyy') : 'NA'}</Body>
  }, [])

  const columns = useMemo(() => {
    return [{
      accessor: 'app_id',
      Header: 'Products',
      Cell: (props: any) => <ProductNameRenderer value={props.value} {...{ ...props, apps }} instances={instances} />,
      width: 170
    },
    {
      accessor: 'provisioning_status',
      Header: 'Status',
      Cell: (props: any) => <StatusRenderer{...{ ...props }} />,
      width: 160
    },
    {
      accessor: 'entitlements',
      Header: 'License',
      Cell: (props: any) => <LicenseRenderer {...{ ...props, apps }} />,
      width: 320
    },
    {
      accessor: 'license_expiration',
      Header: 'Expiration Date',
      Cell: (props: any) => <>{getExpirationDates(props)}</>,
      width: 160
    },
    {
      accessor: 'region',
      Header: 'Region',
      Cell: (props: any) => <Body size='xs'>{getRegionDisplayName(props.row)}</Body>,
      width: 110,
    },
    ]
  }, [apps, getExpirationDates, getRegionDisplayName, instances])

  const getTsgInfo = useMemo(() => {
    if (!tsg) return title
    return `${title} - ( ${tsg?.display_name} - ${tsg?.id} )`
  }, [title, tsg])

  return tsg ? <TableWrapper>
    <TableProvider
      columns={columns}
      data={transferLicenseToPA(tableData)}
      enableColumnSort
      enableResizeColumns
      isLoading={loading}
    >
      <ConnectedTableLayout
        overflow
        enableHeaderTitle={true}
        controlLeft={<HeaderTitle title={getTsgInfo} />}
        sidecar={<div />}
        resultsSummary={<Body size='sm'>{`${transferLicenseToPA(tableData).length} results`}</Body>}
      >
        <ConnectedTable
          emptyStateProps={{
            heading: 'No Service Running',
          }}
        />
      </ConnectedTableLayout>
    </TableProvider>
  </TableWrapper> : <></>
}

export default InstanceTable
